import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  getDrillsRequest,
  updateDrillRequest,
  addPlayerToDrillRequest,
  removePlayerFromDrillRequest,
  setPlayerBibInDrillRequest,
  startDrillRequest,
  stopDrillRequest,
  getDrillPlayerBreakdownRequest,
  createDrill
} from './api'

import {
  startLoading,
  stopLoading,
  setDrills,
  updateDrill,
  updateDrillPlayerBreakdowns,
  addDrill,
  selectDrill
} from './slice'
import { handleAPIError, setError } from '../../ui/error/actions'
import {
  FormattedDrill,
  PlayerBib,
  RawDrill,
  Region,
  UpdateDrillRequestBody,
  UpdatePlayerInDrillRequestBody
} from './types'
import { RawSessionData } from '../sessions/types'
import {
  getFormattedSession,
  getPlayerIdsInSession
} from '../sessions/functions'
import { getSport } from '../sports/functions'
import { getDefaultRegion } from './functions'
import { UnitSystemTypeValues } from '../units/types'

export type CreateNewDrillArgs = {
  quickStart: boolean
  name: string
}

export const createNewDrill = createAsyncThunk(
  'createNewDrill',
  async (
    { quickStart, name }: { quickStart: boolean; name: string },
    { dispatch, getState }: { dispatch: any; getState: any }
  ) => {
    const { drills, sessions } = getState()

    // Find previous drill //
    let prevDrill: FormattedDrill | undefined
    Object.values(drills.processedData).forEach((drill: any) => {
      if (!prevDrill) {
        prevDrill = drill
        return
      }
      if (
        prevDrill &&
        drill.endTime.unixSeconds > prevDrill.endTime.unixSeconds
      ) {
        prevDrill = drill
      }
    })

    // Generate playerIds from session //
    const session = sessions.rawData[sessions.selectedId] as RawSessionData
    const sport = getSport(session.sportType)
    const playerIds = getPlayerIdsInSession(session)

    let playerBibs: PlayerBib = {}
    let region: Region
    // Default bib team direction
    let bibTeamDirection = [2, 3]

    if (
      quickStart &&
      prevDrill &&
      Object.keys(prevDrill.playerBibs || {}).length > 0
    ) {
      // For quick start with a valid previous drill, use only those players
      playerBibs = { ...prevDrill.playerBibs }

      // Use previous drill's region
      region =
        prevDrill.region ||
        getDefaultRegion(session.pitch.pitch.coordinates, sport)

      // Use previous drill's bibTeamDirection if available
      if (
        prevDrill.extraInfo.bibTeamDirection &&
        Array.isArray(prevDrill.extraInfo.bibTeamDirection)
      ) {
        bibTeamDirection = prevDrill.extraInfo.bibTeamDirection
      }
    } else if (quickStart) {
      // For quick start with no previous drill or empty previous drill
      // Add all session players with bib 1
      playerIds.forEach((playerId) => {
        playerBibs[playerId] = 1
      })

      region = getDefaultRegion(session.pitch.pitch.coordinates, sport)
    } else {
      // For regular drill (not quick start)
      // Leave playerBibs empty and use default region
      region = getDefaultRegion(session.pitch.pitch.coordinates, sport)
    }

    const body = {
      name,
      sessionId: session.id,
      region,
      playerBibs,
      bibs: sport.props.features.bibs,
      bibTeamDirection
    }

    dispatch(startLoading())

    try {
      const response = await createDrill(body)
      // Add the new drill to the store
      const formattedSession = getFormattedSession(session.id, getState())
      dispatch(addDrill({ drill: response.data, formattedSession }))
      // If quickStart is true, start the drill immediately
      if (quickStart) {
        dispatch(startDrill(response.data.id))
      }
      dispatch(selectDrill(response.data.id))
      dispatch(stopLoading())
      return response.data
    } catch (error) {
      dispatch(handleAPIError(error, 'errorDetail'))
      dispatch(stopLoading())
      return error
    }
  }
)

export const duplicateDrill = createAsyncThunk(
  'duplicateDrill',
  async (
    drillId: string,
    { dispatch, getState }: { dispatch: any; getState: any }
  ) => {
    const { drills } = getState()

    // Get the drill to duplicate
    const sourceDrill = drills.processedData[drillId]

    if (!sourceDrill) {
      throw new Error('Drill not found')
    }

    // Create a new name based on the original
    const drillName = `${sourceDrill.name} (Copy)`

    // Create request body using source drill data
    const body = {
      name: drillName,
      sessionId: sourceDrill.sessionId,
      region: sourceDrill.region,
      playerBibs: { ...sourceDrill.playerBibs }, // Create a copy of player bibs
      bibs: sourceDrill.rawData.extraInfo.bibs,
      bibTeamDirection: sourceDrill.rawData.extraInfo.bibTeamDirection
    }

    dispatch(startLoading())

    try {
      const response = await createDrill(body)
      // Add the new drill to the store
      const formattedSession = getFormattedSession(
        sourceDrill.sessionId,
        getState()
      )
      dispatch(addDrill({ drill: response.data, formattedSession }))
      dispatch(selectDrill(response.data.id))
      dispatch(stopLoading())
      return response.data
    } catch (error) {
      dispatch(operationFailed(error, 'Failed to duplicate drill'))
      dispatch(stopLoading())
      return error
    }
  }
)

export const getDrillsForSession = createAsyncThunk(
  'getDrillsForSession',
  async (
    sessionId: string,
    { dispatch, getState }: { dispatch: any; getState: any }
  ) => {
    dispatch(startLoading())
    try {
      const response = await getDrillsRequest(sessionId)
      const formattedSession = getFormattedSession(sessionId, getState())
      dispatch(setDrills({ drills: response.data, formattedSession }))
    } catch (error) {
      dispatch(operationFailed(error, 'Failed to get Session Drills'))
      dispatch(stopLoading())
    }
  }
)

export const updateExistingDrill = createAsyncThunk(
  'updateExistingDrill',
  async (data: UpdateDrillRequestBody, { dispatch }) => {
    dispatch(startLoading())
    try {
      const response = await updateDrillRequest(data)
      dispatch(handleDrillUpdate(response.data))
    } catch (error) {
      dispatch(operationFailed(error, 'Failed to Update Drill.'))
      dispatch(stopLoading())
    }
  }
)

// Add player to a drill?
export const addPlayerToDrill = createAsyncThunk(
  'addPlayerToDrill',
  async (data: UpdatePlayerInDrillRequestBody, { dispatch }) => {
    dispatch(startLoading())
    try {
      const response = await addPlayerToDrillRequest(data)
      dispatch(handleDrillUpdate(response.data))
    } catch (error) {
      dispatch(operationFailed(error, 'Failed to add player to drill'))
      dispatch(stopLoading())
    }
  }
)

// Remove player from a drill

export const removePlayerFromDrill = createAsyncThunk(
  'drills/removePlayerFromDrill',
  async (data: UpdatePlayerInDrillRequestBody, { dispatch }) => {
    dispatch(startLoading())
    try {
      const response = await removePlayerFromDrillRequest(data)
      dispatch(handleDrillUpdate(response.data))
    } catch (error) {
      dispatch(operationFailed(error, 'Failed to remove player drom drill'))
      dispatch(stopLoading())
    }
  }
)

// Set player bib in a drill
export const setPlayerBibInDrillThunk = createAsyncThunk(
  'setBibInDrill',
  async (data: UpdatePlayerInDrillRequestBody, { dispatch }) => {
    dispatch(startLoading())
    try {
      const response = await setPlayerBibInDrillRequest(data)
      dispatch(handleDrillUpdate(response.data))
    } catch (error) {
      dispatch(operationFailed(error, 'Failed to set player bib in drill'))
      dispatch(stopLoading())
    }
  }
)

// Start drill
export const startDrill = createAsyncThunk(
  'startDrill',
  async (id: string, { dispatch }) => {
    dispatch(startLoading())
    try {
      const response = await startDrillRequest(id)
      dispatch(handleDrillUpdate(response.data))
    } catch (error) {
      dispatch(operationFailed(error, 'Failed to start drill'))
      dispatch(stopLoading())
    }
  }
)

// Stop drill
export const stopDrill = createAsyncThunk(
  'stopDrill',
  async (id: string, { dispatch }) => {
    dispatch(startLoading())
    try {
      const response = await stopDrillRequest(id)
      dispatch(handleDrillUpdate(response.data))
    } catch (error) {
      dispatch(operationFailed(error, 'Failed to stop drill'))
      dispatch(stopLoading())
    }
  }
)

// Get player breakdown for a drill
export const getPlayerBreakdownForDrill = createAsyncThunk(
  'getPlayerBreakdownForDrill',
  async (
    {
      drillId,
      unitSystemValue
    }: { drillId: string; unitSystemValue: UnitSystemTypeValues },
    { dispatch }
  ) => {
    try {
      const response = await getDrillPlayerBreakdownRequest({
        drillId,
        unitSystemValue
      })
      dispatch(
        updateDrillPlayerBreakdowns({
          drillId,
          rawPlayerBreakdowns: response.data
        })
      )
    } catch (error) {
      dispatch(
        operationFailed(error, 'Failed to get player breakdown for drill')
      )
    }
  }
)

export const operationFailed = (error: any, message: string) => {
  console.log('error', error)
  return setError({
    message: error?.response?.data?.detail
      ? error.response.data.detail
      : message
  })
}

export const handleDrillUpdate = (drill: RawDrill) => {
  return (dispatch, getState) => {
    const formattedSession = getFormattedSession(drill.sessionId, getState())
    dispatch(updateDrill({ drill, formattedSession }))
  }
}
